import axios from "axios"
import moment from "moment"
import { Component } from "react"
import { type RouteComponentProps, withRouter } from "react-router-dom"
import AxiosConfig from "../../../utilities/AxiosConfig"
import LeaseImport from "./LeaseImport/LeaseImport"
import MonthlyEntry from "./MonthlyEntry/MonthlyEntry"
import RequestsManager from "./RequestsManager/RequestsManager"
import Summary from "./Summary/Summary"
import "./LeaseTracking.css"
import { Button, FormControl, Table } from "react-bootstrap"

type LeaseTrackingTypes = {
	fullCalculations: any
	stateCalculations: any
	newLeasesByState: any
	termLeasesByState: any
	invalidStateLeases: any
	activeView: string
	stateCorrections: { [vehicleInformationId: number]: string }
}

class LeaseTracking extends Component<RouteComponentProps, LeaseTrackingTypes> {
	constructor(props: RouteComponentProps) {
		super(props)
		this.state = {
			fullCalculations: null,
			stateCalculations: null,
			newLeasesByState: null,
			termLeasesByState: null,
			invalidStateLeases: null,
			activeView: this.getViewFromHash(props.location.hash),
			stateCorrections: {},
		}
	}

	getViewFromHash = (hash: string): string => {
		const view = hash.replace("#", "")
		if (view === "monthly-entry") return "monthly-entry"
		if (view === "requests") return "requests"
		if (view === "import") return "import"
		return "summary"
	}

	componentDidMount() {
		this.getCalculations()
	}

	componentDidUpdate(prevProps: RouteComponentProps) {
		if (prevProps.location.hash !== this.props.location.hash) {
			this.setState({
				activeView: this.getViewFromHash(this.props.location.hash),
			})
		}
	}

	getCalculations = () => {
		axios
			.get(
				`${process.env.REACT_APP_API_URL}/api/fleet/leases/schedules/summary`,
				AxiosConfig.configValue("min"),
			)
			.then((res) => {
				this.setState({
					fullCalculations: res.data.FleetSchedule,
					stateCalculations: res.data.StateSchedules,
					newLeasesByState: res.data.NewLeasesByState,
					termLeasesByState: res.data.TerminatedLeasesByState,
					invalidStateLeases: res.data.InvalidStateLeases,
				})
			})
			.catch((err) => {
				AxiosConfig.handleError(err)
			})
	}

	getStatusColor = (status: string) => {
		if (status === "Active")
			return { backgroundColor: "#d4edda", color: "inherit" }
		if (status === "Terminated")
			return { backgroundColor: "#f8d7da", color: "#721c24" }
		return { backgroundColor: "inherit", color: "inherit" }
	}

	handleStateChange = (vehicleInformationId: number, state: string) => {
		this.setState((prev) => ({
			stateCorrections: {
				...prev.stateCorrections,
				[vehicleInformationId]: state,
			},
		}))
	}

	saveStateCorrections = () => {
		const promises = Object.entries(this.state.stateCorrections).map(
			([vehicleInformationId, state]) =>
				axios.post(
					`${process.env.REACT_APP_API_URL}/api/fleet/createOrUpdateVehicleInformation`,
					{
						VehicleInformationId: parseInt(vehicleInformationId, 10),
						State: state,
					},
					AxiosConfig.configValue("min"),
				),
		)
		Promise.all(promises)
			.then(() => {
				this.setState({ stateCorrections: {} })
				this.getCalculations()
			})
			.catch((err) => AxiosConfig.handleError(err))
	}

	render() {
		return (
			<div className="main-content col-sm">
				<h3 className="lease-tracking-title">Lease Tracking</h3>
				{this.state.activeView === "requests" && <RequestsManager />}
				{this.state.activeView === "import" && <LeaseImport />}
				{(this.state.activeView === "summary" || this.state.activeView === "monthly-entry") &&
					this.state.invalidStateLeases?.length === 0 && (
						<div className="valid-states">
							{this.state.activeView === "summary" && (
								<Summary
									fullCalculations={this.state.fullCalculations}
									stateCalculations={this.state.stateCalculations}
								/>
							)}
							{this.state.activeView === "monthly-entry" && (
								<MonthlyEntry
									fullCalculations={this.state.fullCalculations}
									stateCalculations={this.state.stateCalculations}
									newLeasesByState={this.state.newLeasesByState}
									termLeasesByState={this.state.termLeasesByState}
								/>
							)}
						</div>
					)}
				{(this.state.activeView === "summary" || this.state.activeView === "monthly-entry") && this.state.invalidStateLeases?.length > 0 && (
					<div className="invalid-states">
						<h4>
							{this.state.invalidStateLeases.length} leases with invalid states
							found. Please review and rectify details below.
						</h4>
						<Table striped bordered hover>
							<thead>
								<tr>
									<th>Unit Number</th>
									<th>Status</th>
									<th>Date Received</th>
									<th>Lease Length</th>
									<th>Current State</th>
									<th>Update State</th>
								</tr>
							</thead>
							<tbody>
								{this.state.invalidStateLeases.map((lease: any) => (
									<tr key={lease.VehicleLeaseId}>
										<td>{lease.UnitNumber}</td>
										<td style={this.getStatusColor(lease.Status)}>
											{lease.Status}
										</td>
										<td>{moment(lease.DateReceived).format("MM/DD/YYYY")}</td>
										<td>{lease.LeaseLength}</td>
										<td>{lease.State}</td>
										<td>
											<FormControl
												as="select"
												value={
													this.state.stateCorrections[
														lease.VehicleInformationId
													] || ""
												}
												onChange={(e) =>
													this.handleStateChange(
														lease.VehicleInformationId,
														e.target.value,
													)
												}
											>
												<option value="">Select State</option>
												<option value="AL">AL (Alabama)</option>
												<option value="AK">AK (Alaska)</option>
												<option value="AZ">AZ (Arizona)</option>
												<option value="AR">AR (Arkansas)</option>
												<option value="CA">CA (California)</option>
												<option value="CO">CO (Colorado)</option>
												<option value="CT">CT (Connecticut)</option>
												<option value="DE">DE (Delaware)</option>
												<option value="DC">DC (District of Columbia)</option>
												<option value="FL">FL (Florida)</option>
												<option value="GA">GA (Georgia)</option>
												<option value="HI">HI (Hawaii)</option>
												<option value="ID">ID (Idaho)</option>
												<option value="IL">IL (Illinois)</option>
												<option value="IN">IN (Indiana)</option>
												<option value="IA">IA (Iowa)</option>
												<option value="KS">KS (Kansas)</option>
												<option value="KY">KY (Kentucky)</option>
												<option value="LA">LA (Louisiana)</option>
												<option value="ME">ME (Maine)</option>
												<option value="MD">MD (Maryland)</option>
												<option value="MA">MA (Massachusetts)</option>
												<option value="MI">MI (Michigan)</option>
												<option value="MN">MN (Minnesota)</option>
												<option value="MS">MS (Mississippi)</option>
												<option value="MO">MO (Missouri)</option>
												<option value="MT">MT (Montana)</option>
												<option value="NE">NE (Nebraska)</option>
												<option value="NV">NV (Nevada)</option>
												<option value="NH">NH (New Hampshire)</option>
												<option value="NJ">NJ (New Jersey)</option>
												<option value="NM">NM (New Mexico)</option>
												<option value="NY">NY (New York)</option>
												<option value="NC">NC (North Carolina)</option>
												<option value="ND">ND (North Dakota)</option>
												<option value="OH">OH (Ohio)</option>
												<option value="OK">OK (Oklahoma)</option>
												<option value="OR">OR (Oregon)</option>
												<option value="PA">PA (Pennsylvania)</option>
												<option value="RI">RI (Rhode Island)</option>
												<option value="SC">SC (South Carolina)</option>
												<option value="SD">SD (South Dakota)</option>
												<option value="TN">TN (Tennessee)</option>
												<option value="TX">TX (Texas)</option>
												<option value="UT">UT (Utah)</option>
												<option value="VT">VT (Vermont)</option>
												<option value="VA">VA (Virginia)</option>
												<option value="WA">WA (Washington)</option>
												<option value="WV">WV (West Virginia)</option>
												<option value="WI">WI (Wisconsin)</option>
												<option value="WY">WY (Wyoming)</option>
											</FormControl>
										</td>
									</tr>
								))}
							</tbody>
						</Table>
						<Button
							variant="primary"
							disabled={
								Object.keys(this.state.stateCorrections).length !==
									this.state.invalidStateLeases.length ||
								Object.values(this.state.stateCorrections).some((v) => !v)
							}
							onClick={this.saveStateCorrections}
						>
							Save Changes
						</Button>
					</div>
				)}
			</div>
		)
	}
}

export default withRouter(LeaseTracking)
